import React, { useEffect, useMemo, useState } from "react";
import { adminApi, adminRdvApi, formatApiError } from "@/lib/api";
import { CalendarCheck, Plus, Edit3, Trash2, Send, Search, Clock, MapPin, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "planifie", label: "Planifié" },
  { value: "termine", label: "Terminé" },
  { value: "annule", label: "Annulé" },
];

const RDV_TYPES = [
  "Rendez-vous préparation",
  "Repérage du lieu",
  "Prestation",
  "Signature de contrat",
  "Restitution photos",
  "Autre",
];

const EMPTY = {
  user_id: "",
  date: "",
  duration_minutes: 60,
  lieu: "",
  type: "Rendez-vous préparation",
  notes: "",
  status: "planifie",
};

function fmtLong(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" });
  } catch { return iso; }
}
function fmtShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function toDatetimeLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

const STATUS_COLORS = {
  planifie: "text-[#DCB457] border-[#DCB457]/40",
  termine: "text-emerald-300 border-emerald-400/40",
  annule: "text-red-300 border-red-400/40",
};

export default function AdminRDV() {
  const [rdvs, setRdvs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {form...} or null
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("upcoming");

  const reload = () => {
    setLoading(true);
    Promise.all([
      adminRdvApi.list().then(setRdvs).catch((e) => { toast.error(formatApiError(e)); return []; }),
      adminApi.users().then((u) => setClients(u.filter((x) => x.role === "client"))).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const now = Date.now();
  const upcoming = useMemo(() => rdvs.filter((r) => r.status !== "annule" && new Date(r.date).getTime() >= now).sort((a, b) => new Date(a.date) - new Date(b.date)), [rdvs, now]);
  const past = useMemo(() => rdvs.filter((r) => r.status === "termine" || (r.status !== "annule" && new Date(r.date).getTime() < now)).sort((a, b) => new Date(b.date) - new Date(a.date)), [rdvs, now]);
  const cancelled = useMemo(() => rdvs.filter((r) => r.status === "annule").sort((a, b) => new Date(b.date) - new Date(a.date)), [rdvs]);

  const currentList = tab === "past" ? past : tab === "cancelled" ? cancelled : upcoming;
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return currentList;
    return currentList.filter((r) => [r.client_name, r.client_email, r.type, r.lieu, r.notes].filter(Boolean).some((v) => String(v).toLowerCase().includes(f)));
  }, [currentList, filter]);

  const save = async () => {
    if (!editing) return;
    if (!editing.user_id) { toast.error("Sélectionnez un client."); return; }
    if (!editing.date) { toast.error("Renseignez la date et l'heure."); return; }
    setSaving(true);
    try {
      const dt = new Date(editing.date);
      if (Number.isNaN(dt.getTime())) throw new Error("Date invalide");
      const payload = { ...editing, date: dt.toISOString(), duration_minutes: Number(editing.duration_minutes) || 60 };
      if (editing.id) {
        // Update
        const { user_id, ...updatable } = payload; // eslint-disable-line no-unused-vars
        await adminRdvApi.update(editing.id, updatable);
        toast.success("RDV mis à jour — le client a été notifié par email.");
      } else {
        await adminRdvApi.create(payload);
        toast.success("RDV créé — email envoyé au client.");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Supprimer ce RDV ? Le client "${r.client_name}" recevra un email d'annulation.`)) return;
    try {
      await adminRdvApi.remove(r.id);
      toast.success("RDV supprimé — email d'annulation envoyé.");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remind = async (r) => {
    try {
      await adminRdvApi.remind(r.id);
      toast.success("Rappel renvoyé au client.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-rdv">
      <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Agenda</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-2">
            Mes <span className="italic gold-text">rendez-vous.</span>
          </h1>
          <p className="text-white/55 text-sm mt-3 max-w-2xl">
            Planifiez, modifiez ou annulez les RDV clients — chaque action envoie automatiquement un email de notification. Rappel 24 h avant.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY, date: toDatetimeLocal(new Date().toISOString()) })}
          data-testid="admin-rdv-new"
          className="btn-gold inline-flex items-center gap-2"
        >
          <Plus size={14} strokeWidth={1.5} /> Nouveau RDV
        </button>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card-luxe p-5 text-center"><p className="eyebrow mb-2">À venir</p><p className="font-serif-luxe text-3xl gold-text">{upcoming.length}</p></div>
        <div className="card-luxe p-5 text-center"><p className="eyebrow mb-2">Passés</p><p className="font-serif-luxe text-3xl">{past.length}</p></div>
        <div className="card-luxe p-5 text-center"><p className="eyebrow mb-2">Annulés</p><p className="font-serif-luxe text-3xl text-red-300/85">{cancelled.length}</p></div>
        <div className="card-luxe p-5 text-center"><p className="eyebrow mb-2">Total</p><p className="font-serif-luxe text-3xl">{rdvs.length}</p></div>
      </div>

      {/* Search */}
      <div className="mb-6 relative max-w-md">
        <Search size={14} strokeWidth={1.4} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="admin-rdv-search"
          placeholder="Rechercher par client, type, lieu…"
          className="w-full bg-black/60 border border-[#DCB457]/25 pl-9 pr-3 py-2 text-sm focus:border-[#DCB457] outline-none"
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#DCB457]/15 mb-6 overflow-x-auto" data-testid="admin-rdv-tabs">
        {[
          ["upcoming", `À venir (${upcoming.length})`],
          ["past", `Historique (${past.length})`],
          ["cancelled", `Annulés (${cancelled.length})`],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            data-testid={`admin-rdv-tab-${k}`}
            className={`px-4 py-3 text-[11px] uppercase tracking-[0.22em] border-b-2 -mb-px transition ${
              tab === k ? "border-[#DCB457] text-[#DCB457]" : "border-transparent text-white/55 hover:text-white/80"
            }`}
          >{label}</button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-white/55 text-sm italic flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="card-luxe p-10 text-center">
          <CalendarCheck size={30} strokeWidth={1} className="text-[#DCB457]/60 mx-auto mb-3" />
          <p className="text-white/60 text-sm">Aucun RDV {tab === "past" ? "passé" : tab === "cancelled" ? "annulé" : "à venir"}.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-rdv-list">
          {filtered.map((r) => (
            <li key={r.id} className="card-luxe p-5 hover:border-[#DCB457]/45 transition" data-testid={`admin-rdv-item-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className={`inline-block text-[10px] uppercase tracking-[0.22em] border px-2 py-0.5 ${STATUS_COLORS[r.status] || STATUS_COLORS.planifie}`}>
                      {STATUS_OPTIONS.find((s) => s.value === r.status)?.label || "Planifié"}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#DCB457]/85">{r.type || "Rendez-vous"}</span>
                  </div>
                  <p className="font-serif-luxe text-base md:text-lg">
                    {r.client_name || "—"} <span className="text-white/40 text-xs">· {r.client_email}</span>
                  </p>
                  <p className="text-[#DCB457] text-sm mt-1">{fmtLong(r.date)}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-white/60 flex-wrap">
                    <span className="inline-flex items-center gap-1.5"><Clock size={12} strokeWidth={1.4} /> {r.duration_minutes || 60} min</span>
                    {r.lieu && <span className="inline-flex items-center gap-1.5"><MapPin size={12} strokeWidth={1.4} /> {r.lieu}</span>}
                  </div>
                  {r.notes && <p className="text-white/55 text-xs mt-2 italic border-l-2 border-[#DCB457]/20 pl-3">{r.notes}</p>}
                </div>
                <div className="inline-flex flex-wrap gap-2 shrink-0">
                  {r.status !== "annule" && r.status !== "termine" && (
                    <button type="button" onClick={() => remind(r)} title="Renvoyer un rappel" data-testid={`admin-rdv-remind-${r.id}`} className="p-2 border border-[#DCB457]/25 text-[#DCB457] hover:bg-[#DCB457]/10">
                      <Send size={12} strokeWidth={1.5} />
                    </button>
                  )}
                  <button type="button" onClick={() => setEditing({ ...r, date: toDatetimeLocal(r.date) })} title="Modifier" data-testid={`admin-rdv-edit-${r.id}`} className="p-2 border border-white/15 text-white/60 hover:bg-white/5">
                    <Edit3 size={12} strokeWidth={1.5} />
                  </button>
                  <button type="button" onClick={() => del(r)} title="Supprimer" data-testid={`admin-rdv-delete-${r.id}`} className="p-2 border border-red-400/30 text-red-300 hover:bg-red-500/10">
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#0a0a0a] border border-[#DCB457]/30 max-w-2xl w-full p-6 md:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-rdv-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="eyebrow mb-1">{editing.id ? "Modifier" : "Nouveau"} RDV</p>
                <h2 className="font-serif-luxe text-2xl md:text-3xl">
                  {editing.id ? "Modifier le rendez-vous" : "Planifier un rendez-vous"}
                </h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-2 text-white/50 hover:text-white/90"><X size={16} strokeWidth={1.5} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-1.5 block">Client *</label>
                <select
                  className="input-luxe"
                  value={editing.user_id}
                  onChange={(e) => setEditing({ ...editing, user_id: e.target.value })}
                  data-testid="admin-rdv-modal-client"
                  disabled={!!editing.id}
                >
                  <option value="">— Choisir un client —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {[c.prenom, c.nom].filter(Boolean).join(" ").trim() || c.name || c.email} · {c.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-1.5 block">Date & heure *</label>
                  <input
                    type="datetime-local"
                    className="input-luxe"
                    value={editing.date || ""}
                    onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                    data-testid="admin-rdv-modal-date"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-1.5 block">Durée (minutes)</label>
                  <input
                    type="number" min="15" step="15"
                    className="input-luxe"
                    value={editing.duration_minutes || 60}
                    onChange={(e) => setEditing({ ...editing, duration_minutes: e.target.value })}
                    data-testid="admin-rdv-modal-duration"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-1.5 block">Type de RDV</label>
                <select className="input-luxe" value={editing.type || ""} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="admin-rdv-modal-type">
                  {RDV_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-1.5 block">Lieu</label>
                <input
                  className="input-luxe"
                  value={editing.lieu || ""}
                  onChange={(e) => setEditing({ ...editing, lieu: e.target.value })}
                  placeholder="ex. Chez le DJ · 32 rue de la Musique, Lille"
                  data-testid="admin-rdv-modal-lieu"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-1.5 block">Notes internes</label>
                <textarea
                  className="input-luxe min-h-[90px]"
                  value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  data-testid="admin-rdv-modal-notes"
                />
              </div>
              {editing.id && (
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-1.5 block">Statut</label>
                  <select className="input-luxe" value={editing.status || "planifie"} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="admin-rdv-modal-status">
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              )}
              <p className="text-[11px] text-white/40 italic">
                {editing.id ? "Le client sera notifié par email de toute modification." : "Le client recevra un email de confirmation."}
              </p>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#DCB457]/15">
                <button type="button" onClick={() => setEditing(null)} className="text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[#DCB457] px-4 py-2">Annuler</button>
                <button type="button" onClick={save} disabled={saving} data-testid="admin-rdv-modal-save" className="btn-gold inline-flex items-center gap-2 disabled:opacity-60">
                  {saving ? <><Loader2 size={13} className="animate-spin" /> Enregistrement…</> : (editing.id ? "Mettre à jour" : "Créer le RDV")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
